import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Shared Alert Component
 *
 * Displays alerts, notifications, and messages with various types.
 * Can show title, message body, and optional action links.
 *
 * @param {string} type - Alert type: 'error', 'success', 'warning', 'info'
 * @param {string} title - Optional alert title
 * @param {string|React.ReactNode} message - Alert message body (can be string or JSX)
 * @param {Array<{text: string, href: string, onClick: function}>} links - Optional action links
 * @param {boolean} dismissible - Whether the alert can be dismissed
 * @param {function} onDismiss - Callback function when alert is dismissed
 * @param {string} className - Additional CSS classes
 * @param {number} autoCloseDuration - Duration in milliseconds before auto-closing (default: 5000ms, set to null to disable)
 */
const AlertMessage = ({
  type = 'info',
  title,
  message,
  links = [],
  dismissible = false,
  onDismiss,
  className = '',
  autoCloseDuration = 5000
}) => {
  const timeoutRef = useRef(null);
  const onDismissRef = useRef(onDismiss);
  const [progress, setProgress] = useState(100);

  // Keep onDismiss ref up to date
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (autoCloseDuration && onDismissRef.current) {
      // Set up auto-close timer
      timeoutRef.current = setTimeout(() => {
        onDismissRef.current?.();
      }, autoCloseDuration);

      // Animate progress bar
      const startTime = Date.now();
      const animationInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / autoCloseDuration) * 100);
        setProgress(remaining);

        if (remaining <= 0) {
          clearInterval(animationInterval);
        }
      }, 16); // ~60fps

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        clearInterval(animationInterval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount
  // Map types to Bootstrap alert classes and icons
  const typeConfig = {
    error: {
      class: 'alert-danger',
      icon: 'fas fa-exclamation-circle',
      progressColor: '#dc3545'
    },
    success: {
      class: 'alert-success',
      icon: 'fas fa-check-circle',
      progressColor: '#198754'
    },
    warning: {
      class: 'alert-warning',
      icon: 'fas fa-exclamation-triangle',
      progressColor: '#ffc107'
    },
    info: {
      class: 'alert-info',
      icon: 'fas fa-info-circle',
      progressColor: '#0dcaf0'
    }
  };

  const config = typeConfig[type] || typeConfig.info;

  // Progress bar styles
  const progressBarStyle = {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '6px',
    height: `${progress}%`,
    backgroundColor: config.progressColor,
    zIndex: 10,
    pointerEvents: 'none',
    opacity: 0.9,
    transition: 'height 0.05s linear'
  };

  return (
    <div
      className={`alert ${config.class} ${dismissible ? 'alert-dismissible fade show' : ''} ${className}`}
      role="alert"
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      {/* Auto-close progress bar */}
      {autoCloseDuration && onDismiss && (
        <div style={progressBarStyle} aria-hidden="true"></div>
      )}

      <div className="d-flex align-items-start">
        {/* Icon */}
        <i className={`${config.icon} me-2 mt-1`} aria-hidden="true"></i>

        {/* Content */}
        <div className="flex-grow-1">
          {/* Title */}
          {title && (
            <h6 className="alert-heading mb-1">
              <strong>{title}</strong>
            </h6>
          )}

          {/* Message */}
          {message && (
            <div className="mb-0">
              {typeof message === 'string' ? (
                <span>{message}</span>
              ) : (
                message
              )}
            </div>
          )}

          {/* Action Links */}
          {links && links.length > 0 && (
            <div className="mt-2">
              {links.map((link, index) => (
                <React.Fragment key={index}>
                  {link.onClick ? (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-underline"
                      onClick={link.onClick}
                    >
                      {link.text}
                    </button>
                  ) : (
                    <a
                      href={link.href}
                      className="alert-link"
                      target={link.newTab ? '_blank' : '_self'}
                      rel={link.newTab ? 'noopener noreferrer' : undefined}
                    >
                      {link.text}
                    </a>
                  )}
                  {index < links.length - 1 && <span className="mx-2">|</span>}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Dismiss button */}
        {dismissible && (
          <button
            type="button"
            className="btn-close"
            onClick={onDismiss}
            aria-label="Close"
          ></button>
        )}
      </div>
    </div>
  );
};

AlertMessage.propTypes = {
  type: PropTypes.oneOf(['error', 'success', 'warning', 'info']),
  title: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  links: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      href: PropTypes.string,
      onClick: PropTypes.func,
      newTab: PropTypes.bool
    })
  ),
  dismissible: PropTypes.bool,
  onDismiss: PropTypes.func,
  className: PropTypes.string,
  autoCloseDuration: PropTypes.number
};

export default AlertMessage;
